import React, { useCallback, useMemo } from 'react';
import {
  BaseEdge,
  EdgeLabelRenderer,
  EdgeProps,
  getSmoothStepPath,
} from '@xyflow/react';
import { PlusIcon } from 'lucide-react';

const EDGE_TRANSITION =
  'stroke 120ms ease, stroke-width 120ms ease, opacity 120ms ease';

const CustomEdge = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  targetHandle,
  style = {},
  markerEnd,
  data,
  selected = false,
}: EdgeProps & { targetHandle?: string }) => {
  const branch = (data as { branch?: 'true' | 'false' } | undefined)?.branch;
  const isPreferred =
    (data as { isPreferred?: boolean } | undefined)?.isPreferred || false;
  const isDraggingPalette =
    (data as { isDraggingPalette?: boolean } | undefined)?.isDraggingPalette ||
    false;
  const isDropCandidate =
    (data as { isDropCandidate?: boolean } | undefined)?.isDropCandidate ||
    false;
  const isNodeDragCandidate =
    (data as { isNodeDragCandidate?: boolean } | undefined)
      ?.isNodeDragCandidate || false;
  const shouldDeemphasize =
    (data as { shouldDeemphasize?: boolean } | undefined)?.shouldDeemphasize ||
    false;
  const isNodeDragMode =
    (data as { isNodeDragMode?: boolean } | undefined)?.isNodeDragMode || false;
  const hideWhileDraggingIncident =
    (data as { hideWhileDraggingIncident?: boolean } | undefined)
      ?.hideWhileDraggingIncident || false;
  const skeletonWidth =
    (data as { skeletonWidth?: number } | undefined)?.skeletonWidth ?? 350;
  const skeletonHeight =
    (data as { skeletonHeight?: number } | undefined)?.skeletonHeight ?? 170;
  const routing = (
    data as
      | { routing?: { stepPosition?: number; laneOffset?: number } }
      | undefined
  )?.routing;
  const isBranchEdge = branch === 'true' || branch === 'false';
  const branchColor =
    branch === 'true' ? 'hsl(var(--primary))' : 'hsl(var(--muted-foreground))';
  const isMergeEntryEdge =
    targetHandle === 'merge-true' || targetHandle === 'merge-false';

  const stepPosition = isBranchEdge
    ? 0.5
    : typeof routing?.stepPosition === 'number'
      ? routing.stepPosition
      : isMergeEntryEdge
        ? 0.72
        : 0.5;
  const laneOffset =
    typeof routing?.laneOffset === 'number' ? routing.laneOffset : 0;
  const centerX = sourceX + (targetX - sourceX) / 2 + laneOffset;

  const [edgePath, labelX, labelY] = useMemo(
    () =>
      getSmoothStepPath({
        sourceX,
        sourceY,
        sourcePosition,
        targetX,
        targetY,
        targetPosition,
        borderRadius: 18,
        offset: 18,
        stepPosition,
        centerX,
      }),
    [
      centerX,
      sourcePosition,
      sourceX,
      sourceY,
      stepPosition,
      targetPosition,
      targetX,
      targetY,
    ]
  );

  const handleAddNode = useCallback(() => {
    const event = new CustomEvent('addNodeFromEdge', {
      detail: {
        edgeId: id,
      },
    });
    window.dispatchEvent(event);
  }, [id]);

  const stopEdgeButtonPropagation = useCallback(
    (
      event:
        | React.MouseEvent<HTMLButtonElement>
        | React.PointerEvent<HTMLButtonElement>
    ) => {
      event.preventDefault();
      event.stopPropagation();
    },
    []
  );

  const handleButtonClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      handleAddNode();
    },
    [handleAddNode]
  );

  const branchLabelX = sourceX + (targetX - sourceX) * 0.33;
  const branchLabelY = sourceY + (targetY - sourceY) * 0.33;
  const isMostlyVertical = Math.abs(targetX - sourceX) < 24;
  const normalButtonLaneOffset =
    isMostlyVertical && laneOffset !== 0 ? Math.sign(laneOffset) * -10 : 0;
  const addButtonX = labelX + (isBranchEdge ? 0 : normalButtonLaneOffset);
  const addButtonY = labelY;
  const showDropSkeleton = isDropCandidate || isNodeDragCandidate;
  const edgeControlZIndex = showDropSkeleton ? 0 : 1000;

  const baseEdgeStyle = useMemo(
    () =>
      hideWhileDraggingIncident
        ? {
            strokeWidth: 0,
            strokeOpacity: 0,
            pointerEvents: 'none' as const,
            transition: EDGE_TRANSITION,
            ...style,
          }
        : {
            strokeWidth:
              isDropCandidate || isNodeDragCandidate
                ? 3
                : shouldDeemphasize
                  ? 1
                  : 2,
            strokeOpacity: shouldDeemphasize ? 0.3 : 1,
            stroke:
              selected || isPreferred || isDropCandidate || isNodeDragCandidate
                ? 'hsl(var(--primary))'
                : branchColor,
            transition: EDGE_TRANSITION,
            ...style,
          },
    [
      branchColor,
      hideWhileDraggingIncident,
      isDropCandidate,
      isNodeDragCandidate,
      isPreferred,
      selected,
      shouldDeemphasize,
      style,
    ]
  );

  return (
    <>
      <BaseEdge
        path={edgePath}
        markerEnd={hideWhileDraggingIncident ? undefined : markerEnd}
        style={baseEdgeStyle}
      />
      {!hideWhileDraggingIncident && (
        <EdgeLabelRenderer>
          <>
            {isBranchEdge && (
              <div
                style={{
                  position: 'absolute',
                  transform: `translate(-50%, -50%) translate(${branchLabelX}px,${branchLabelY}px)`,
                  pointerEvents: 'none',
                }}
                className="nodrag nopan"
              >
                <div
                  className="flex items-center justify-center text-xs font-normal"
                  style={{
                    color:
                      branch === 'true' ? 'hsl(var(--primary))' : branchColor,
                  }}
                >
                  {branch === 'true' ? 'TRUE' : 'FALSE'}
                </div>
              </div>
            )}
            <div
              style={{
                position: 'absolute',
                transform: `translate(-50%, -50%) translate(${addButtonX}px,${addButtonY}px)`,
                fontSize: 12,
                pointerEvents: 'all',
                zIndex: edgeControlZIndex,
              }}
              className="nodrag nopan"
            >
              {showDropSkeleton && (
                <div
                  className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-xl border-2 border-dashed border-primary/70 bg-card/70 px-4 py-3 shadow-md backdrop-blur-[2px]"
                  style={{
                    pointerEvents: 'none',
                    width: `${skeletonWidth}px`,
                    height: `${skeletonHeight}px`,
                  }}
                >
                  <div className="relative flex h-full w-full flex-col items-center justify-center gap-2 overflow-hidden rounded-lg">
                    <span className="pointer-events-none absolute inset-0 bg-[linear-gradient(110deg,transparent_0%,hsl(var(--primary)/0.12)_48%,transparent_100%)] animate-[pulse_1.2s_ease-in-out_infinite]" />
                    <div className="relative z-10 flex h-8 w-8 items-center justify-center rounded-full border border-primary/35 bg-primary/15 text-primary">
                      <PlusIcon className="h-4 w-4" />
                    </div>
                    <span className="relative z-10 text-[10px] font-semibold tracking-[0.12em] text-primary/90">
                      DROP NODE HERE
                    </span>
                  </div>
                </div>
              )}
              {(selected ||
                isPreferred ||
                isDropCandidate ||
                isNodeDragCandidate) && (
                <>
                  <div
                    className="absolute inset-0 rounded-full bg-primary/25 animate-ping"
                    style={{ animationDuration: '1.2s' }}
                  />
                  <div
                    className="absolute inset-0 rounded-full bg-primary/15 animate-ping"
                    style={{
                      animationDuration: '1.8s',
                      animationDelay: '150ms',
                    }}
                  />
                </>
              )}
              <button
                type="button"
                onPointerDown={stopEdgeButtonPropagation}
                onMouseDown={stopEdgeButtonPropagation}
                onClick={handleButtonClick}
                className={`relative w-8 h-8 rounded-full flex items-center justify-center shadow-sm transition-colors z-10 border-2 border-primary ${
                  selected ||
                  isPreferred ||
                  isDropCandidate ||
                  isNodeDragCandidate
                    ? 'bg-primary text-primary-foreground hover:opacity-90'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground'
                } ${
                  (isDraggingPalette && !isDropCandidate) || showDropSkeleton
                    ? 'opacity-0 pointer-events-none'
                    : ''
                } ${isNodeDragMode ? 'opacity-0 pointer-events-none' : ''}`}
                title="Add node"
              >
                <PlusIcon
                  className={`w-4 h-4 transition-transform duration-200 ${
                    selected ||
                    isPreferred ||
                    isDropCandidate ||
                    isNodeDragCandidate
                      ? 'scale-110 rotate-90'
                      : ''
                  }`}
                />
              </button>
            </div>
          </>
        </EdgeLabelRenderer>
      )}
    </>
  );
};

export default React.memo(CustomEdge);
